import classNames from 'classnames'
import { FC, JSX, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { toServerFormat } from 'core-components/helpers/date'
import 'core-components/sitebuilder/assets/vars.scss'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { selectCurrentSitePageId } from 'core-components/sitebuilder/store/selectors/selectCurrentSitePageId'
import { selectEditLayoutMode } from 'core-components/sitebuilder/store/selectors/selectEditLayoutMode'
import { selectSitePages } from 'core-components/sitebuilder/store/selectors/selectSitePages'
import { setFlexSettings, setSitePages } from 'core-components/sitebuilder/store/siteBuilderSlice'

import { FlexGridWrapperContext } from './context/FlexGridWrapperContext'
import { FlexGrid } from './FlexGrid'
import { AccessProblemHome } from './flexGridWrapper/accessProblemPage/AccessProblemHome'
import { ApplySettingsArea } from './flexGridWrapper/ApplySettingsArea'
import { THROTTLING_TIMEOUT } from './flexGridWrapper/constants'
import { EmptySitePage } from './flexGridWrapper/EmptySitePage'
import { FlexGridActionWrapper } from './flexGridWrapper/FlexGridActionWrapper'
import { HeadWithActions } from './flexGridWrapper/HeadWithActions'
import { getAllWrappersInPage } from './flexGridWrapper/helpers/getAllWrappersInPage'
import { getVersion } from './flexGridWrapper/helpers/getVersion'
import { getVisualViewPortWidth } from './flexGridWrapper/helpers/getVisualViewPortWidth'
import { mergeTabsSettings } from './flexGridWrapper/helpers/mergeTabsSettings'
import { useFlexSettings } from './flexGridWrapper/hooks/useFlexSettings'
import { useVisibleSettings } from './flexGridWrapper/hooks/useVisibleSettings'
import { NoAccessPage } from './flexGridWrapper/NoAccessPage'
import { getPageSettingsCountOnPage, SettingsButton } from './flexGridWrapper/SettingsButton'
import { SettingsImportModal } from './flexGridWrapper/SettingsImportModal'
import {
  IRenderCustomContentParams,
  IRenderCustomSettingsParams,
} from './flexItem/models/IRenderCustomContent'
import { useCurrentSitePage } from './hooks/useCurrentSitePage'
import { useFlexActions } from './hooks/useFlexActions'

import styles from './FlexGridWrapper.module.scss'

export interface IFlexGridWrapper {
  widgetParams?: Record<string, any>
  area?: string
  className?: string
  flexWidth?: FlexDirectionBreak
  alignTop?: boolean
  settingButtonVisible?: boolean
  SettingButtonView?: FC<any>
  renderCustomWidget?: (data: IRenderCustomContentParams) => JSX.Element | null
  renderCustomSettingsWidget?: (data: IRenderCustomSettingsParams) => JSX.Element | null
}
export const FlexGridWrapper = (props: IFlexGridWrapper) => {
  const { canEditSettings } = useContext(FlexGridWrapperContext)

  const dispatch = useDispatch()
  const sitePages = useSelector(selectSitePages)
  const editLayoutMode = useSelector(selectEditLayoutMode)
  const flexSettings = useFlexSettings()
  const sitePageInitial = useCurrentSitePage()
  const sitePageId = useSelector(selectCurrentSitePageId)
  const isAccessProblem = !sitePageInitial.id && sitePages.find((i) => i.id === sitePageId)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const throttlingTimerRef = useRef<number>(0)
  const visualViewPortWidthRef = useRef<number>(0)

  const [versionVisible, setVersionVisible] = useState<boolean>(true)
  const [showFileDialog, setShowFileDialog] = useState(false)
  const [resize, setResize] = useState(1)
  const [isSettingsVisible, setIsSettingsVisible] = useState<boolean>(!!props.settingButtonVisible)
  const [togglePanelOpen, setTogglePanelOpen] = useState(false)
  const [sitePage, setSitePage] = useState<ISitePage>(sitePageInitial)

  const widgetParams = props.widgetParams || {}
  const version = getVersion(sitePage, flexSettings)

  const currentSettings = structuredClone(flexSettings[`${sitePage.id}-${props.area || 0}`] || {})
  const defaultSettings = structuredClone(
    sitePage.defaultSettings[props.area || 0] || ({} as IFlexLayout)
  )
  const [isDefault, setIsDefault] = useState<boolean>(() => {
    return !(Object.keys(currentSettings).length > 0 && canEditSettings)
  })
  const alertMessageVisible =
    !props.area &&
    !isDefault &&
    Object.keys(getAllWrappersInPage(sitePage.id, flexSettings, 'onlyArea')).length > 0

  const [settings, setSettings] = useState<IFlexLayout>(() => {
    if (Object.keys(currentSettings).length > 0 && canEditSettings) {
      return JSON.parse(JSON.stringify(currentSettings)) as IFlexLayout
    } else {
      if (defaultSettings && defaultSettings.items) {
        const startState = JSON.parse(JSON.stringify(defaultSettings)) as IFlexLayout
        if (!canEditSettings) {
          if (Object.keys(currentSettings).length > 0) {
            mergeTabsSettings(JSON.parse(JSON.stringify(defaultSettings)), currentSettings)
          }
        }
        return startState
      }
    }
    return {} as IFlexLayout
  })

  const { visibleSettings } = useVisibleSettings({ settings })
  useFlexActions({ settings: visibleSettings.settings, area: props.area, sitePageId: sitePage.id })

  const onResize = useCallback(() => {
    clearTimeout(throttlingTimerRef.current)
    throttlingTimerRef.current = window.setTimeout(() => {
      if (visualViewPortWidthRef.current !== getVisualViewPortWidth()) {
        visualViewPortWidthRef.current = getVisualViewPortWidth()
        setResize((prevValue) => prevValue + 1)
      }
    }, THROTTLING_TIMEOUT)
  }, [])

  const onTogglePanelChange = useCallback((value?: boolean) => {
    setTogglePanelOpen((prevValue) => (value === undefined ? !prevValue : value))
  }, [])

  const setStateHandler = useCallback(
    async (newSettings: IFlexLayout | undefined, baseSettings = flexSettings) => {
      if (newSettings) {
        const newFlexSettings = {
          ...baseSettings,
          [`${sitePage.id}-${props.area || 0}`]: {
            ...newSettings,
            version: toServerFormat(new Date()),
          },
        }
        dispatch(setFlexSettings(newFlexSettings))
        setIsDefault(false)
        setSettings(structuredClone(newSettings))
        if (props.area) {
          const nextSitePages = sitePages.filter((i) => i.id !== sitePage.id)
          const newSitePages = [...nextSitePages, { ...sitePage }]

          dispatch(setSitePages(newSitePages))
        }
      }
    },
    [dispatch, flexSettings, props.area, sitePage, sitePages]
  )

  useEffect(() => {
    if (
      props.area &&
      Object.keys(flexSettings[`${sitePage.id}-0`] || {}).length === 0 &&
      isDefault &&
      JSON.stringify(currentSettings) !== JSON.stringify(defaultSettings) &&
      JSON.stringify(settings) !== JSON.stringify(defaultSettings)
    ) {
      setSettings(JSON.parse(JSON.stringify(defaultSettings)))
    }
  }, [currentSettings, defaultSettings, flexSettings, isDefault, props.area, settings, sitePage.id])

  useEffect(() => {
    let timerId: number
    if (!editLayoutMode) {
      setIsSettingsVisible(!!props.settingButtonVisible)
    } else {
      timerId = window.setTimeout(() => {
        const availableCount = !!props.settingButtonVisible ? 1 : 0
        const isVisible = editLayoutMode && getPageSettingsCountOnPage() === availableCount
        setIsSettingsVisible(isVisible)
      }, 100)
    }

    return () => {
      clearTimeout(timerId)
    }
  }, [props.area, props.settingButtonVisible, editLayoutMode])

  useEffect(() => {
    visualViewPortWidthRef.current = getVisualViewPortWidth()
  }, [])

  useEffect(() => {
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  })

  return (
    <FlexGridActionWrapper
      sitePage={sitePage}
      setSitePage={setSitePage}
      setIsDefault={setIsDefault}
      settings={settings}
      setSettings={setSettings}
      area={props.area}
    >
      <div
        ref={wrapperRef}
        className={classNames(styles.root, editLayoutMode ? styles.root__edit : '')}
      >
        {isSettingsVisible && (
          <div
            className={classNames(
              styles.settingsBtnWrap,
              'context-fixed',
              styles['settingsBtnWrap_fixed'],
              editLayoutMode ? styles['settingsBtnWrap--editMode'] : ''
            )}
          >
            {props.SettingButtonView ? <props.SettingButtonView /> : <SettingsButton />}
          </div>
        )}
        <div className={classNames(styles.wrapper, 'flexgrid__topparent')}>
          {!props.area && version && !editLayoutMode && versionVisible ? (
            <ApplySettingsArea version={version} onClose={() => setVersionVisible(false)} />
          ) : null}

          {sitePageInitial.id ? (
            <FlexGrid
              onResize={onResize}
              onTogglePanelChange={onTogglePanelChange}
              togglePanelOpen={togglePanelOpen}
              resize={resize}
              parentResized={true}
              parent={wrapperRef.current}
              alignTop={props.alignTop || false}
              classNameLayout={props.className}
              allowEdit={canEditSettings}
              fullWidth={false}
              additionalHead={
                editLayoutMode ? (
                  <HeadWithActions
                    alertMessageVisible={alertMessageVisible}
                    actionSelectorVisible={!props.area}
                    importSettingsCallback={() => setShowFileDialog(true)}
                  />
                ) : null
              }
              width={props.flexWidth || FlexDirectionBreak.p1212}
              widgetParams={widgetParams}
              visibleSettings={editLayoutMode ? settings : visibleSettings.settings}
              allSettings={settings}
              updateFlexLayout={setStateHandler}
              page={sitePage.id}
              area={props.area || '0'}
              renderCustomWidget={props.renderCustomWidget}
              renderCustomSettingsWidget={props.renderCustomSettingsWidget}
            />
          ) : isAccessProblem ? (
            <NoAccessPage footerContent={AccessProblemHome} />
          ) : (
            <EmptySitePage footerContent={AccessProblemHome} />
          )}
        </div>

        <SettingsImportModal
          visible={showFileDialog}
          storageKey={`${sitePage.id}-${props.area || 0}`}
          onClose={() => {
            setShowFileDialog(false)
          }}
          onSave={(newSettings) => {
            setIsDefault(false)
            setSettings(newSettings)
          }}
        />
      </div>
    </FlexGridActionWrapper>
  )
}
